import {
  Message01Icon,
  RefreshIcon,
  SmartPhone01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useMemo, useState } from "react";
import { Markdown } from "@/components/markdown";
import { SystemDialogueSheet } from "@/components/system-dialogue-sheet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { usePhoneJsonEditorSessionOptional } from "@/components/phone-json-editor/phone-json-editor-session";
import type { CasePreview, CasePreviewLocale } from "@/lib/api";
import { cn } from "@/lib/utils";

const FALLBACK_PHONE_UI_LOCALES = [
  "en",
  "es",
  "tr",
  "de",
  "fr",
  "pt",
  "pt-BR",
  "zh-HK",
  "zh-MO",
  "zh-TW",
  "ja-JP",
  "ko-KR",
] as const;

/**
 * Build phone load URL. Case JSON language and phone UI language are independent:
 * shell chrome comes from /{runtime}/{phoneUiLocale}/, content from caseJsonUrl.
 */
export function buildPreviewIframeSrc(opts: {
  phonePreviewBase: string;
  phoneRuntimeVersion: string | null | undefined;
  phoneUiLocale: string;
  caseJsonUrl: string;
}): string {
  const base = opts.phonePreviewBase.replace(/\/$/, "");
  const raw = (opts.phoneRuntimeVersion ?? "").trim();
  const runtime = !raw || raw === "latest" ? "latest" : raw;
  const phoneUiLocale = (opts.phoneUiLocale || "en").trim() || "en";
  return `${base}/${encodeURIComponent(runtime)}/${encodeURIComponent(phoneUiLocale)}/load?source=${encodeURIComponent(opts.caseJsonUrl)}`;
}

type PhonePreviewProps = {
  preview: CasePreview | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Preferred case JSON locale (e.g. current version locale). */
  preferredLocale?: string | null;
  /** When set, prefer that draft for its locale on first load. */
  preferredVersionId?: string | null;
  /** Bump after a phone JSON save so the iframe reloads the same URL. */
  reloadNonce?: number;
};

export function PhonePreviewPanel({
  preview,
  isLoading,
  isError,
  preferredLocale,
  preferredVersionId,
  reloadNonce = 0,
}: PhonePreviewProps) {
  const editor = usePhoneJsonEditorSessionOptional();
  const besideEditor = editor?.open === true;
  const locales = preview?.locales ?? [];
  const phoneUiLocales =
    preview?.phoneUiLocales?.length
      ? preview.phoneUiLocales
      : [...FALLBACK_PHONE_UI_LOCALES];

  const [caseLocale, setCaseLocale] = useState<string>("");
  /** null = follow preferred/case locale until the operator picks one. */
  const [phoneUiLocalePick, setPhoneUiLocalePick] = useState<string | null>(
    null,
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [dialogueOpen, setDialogueOpen] = useState(false);

  useEffect(() => {
    if (locales.length === 0) {
      setCaseLocale("");
      return;
    }
    setCaseLocale((prev) => {
      if (prev && locales.some((l) => l.locale === prev)) return prev;
      if (
        preferredVersionId &&
        locales.some((l) => l.versionId === preferredVersionId)
      ) {
        return locales.find((l) => l.versionId === preferredVersionId)!.locale;
      }
      if (
        preferredLocale &&
        locales.some((l) => l.locale === preferredLocale)
      ) {
        return preferredLocale;
      }
      return locales[0]!.locale;
    });
  }, [locales, preferredLocale, preferredVersionId]);

  const phoneUiLocale = useMemo(() => {
    if (phoneUiLocalePick && phoneUiLocales.includes(phoneUiLocalePick)) {
      return phoneUiLocalePick;
    }
    // Until the operator picks a phone UI language, follow case JSON language.
    if (caseLocale && phoneUiLocales.includes(caseLocale)) {
      return caseLocale;
    }
    if (preferredLocale && phoneUiLocales.includes(preferredLocale)) {
      return preferredLocale;
    }
    return phoneUiLocales[0] ?? "en";
  }, [phoneUiLocalePick, phoneUiLocales, preferredLocale, caseLocale]);

  const active: CasePreviewLocale | null = useMemo(() => {
    if (!caseLocale) return null;
    return locales.find((l) => l.locale === caseLocale) ?? null;
  }, [locales, caseLocale]);

  const iframeSrc = useMemo(() => {
    if (!active || !preview?.phonePreviewBase) {
      return active?.iframeSrc ?? "";
    }
    return buildPreviewIframeSrc({
      phonePreviewBase: preview.phonePreviewBase,
      phoneRuntimeVersion: active.phoneRuntimeVersion,
      phoneUiLocale,
      caseJsonUrl: active.caseJsonUrl,
    });
  }, [active, preview?.phonePreviewBase, phoneUiLocale]);

  const onCaseLocaleChange = (next: string[]) => {
    const value = next[0];
    if (!value) return;
    setCaseLocale(value);
    setReloadKey((k) => k + 1);
  };

  const onPhoneUiLocaleChange = (next: string[]) => {
    const value = next[0];
    if (!value) return;
    setPhoneUiLocalePick(value);
    setReloadKey((k) => k + 1);
  };

  const guideMarkdown = active?.experienceGuideMarkdown?.trim() || "";
  const guidePresent =
    active?.experienceGuidePresent === true || guideMarkdown.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>手机预览</CardTitle>
          <CardDescription>
            通过 iframe 在 peekie 手机壳中加载草稿 phone JSON（
            {preview?.phonePreviewBase ?? "手机预览主机"}）。可分别切换案例 JSON
            语言与手机 UI 语言。系统对话按钮用同一份草稿模拟客户端引导页与任务问答，无需发布。QA
            核对线索时参考下方线索指南。
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : isError ? (
            <Alert variant="destructive">
              <AlertTitle>预览错误</AlertTitle>
              <AlertDescription>
                无法加载预览配置。
              </AlertDescription>
            </Alert>
          ) : locales.length === 0 ? (
            <Empty className="border border-border py-10">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <HugeiconsIcon icon={SmartPhone01Icon} />
                </EmptyMedia>
                <EmptyTitle>还没有 phone JSON</EmptyTitle>
                <EmptyDescription>
                  请先跑完流水线直到产生草稿手机包。预览会列出版本上已有 phone
                  JSON 的语言。
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap items-end gap-4">
                  <Field className="w-fit">
                    <FieldLabel id="preview-case-locale-label">
                      案例 JSON 语言
                    </FieldLabel>
                    <ToggleGroup
                      aria-labelledby="preview-case-locale-label"
                      variant="outline"
                      spacing={2}
                      value={caseLocale ? [caseLocale] : []}
                      onValueChange={onCaseLocaleChange}
                    >
                      {locales.map((l) => (
                        <ToggleGroupItem key={l.locale} value={l.locale}>
                          {l.locale}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    <FieldDescription>
                      {active
                        ? `v${active.versionN} · ${active.status} · 运行时 ${active.phoneRuntimeVersion}`
                        : null}
                    </FieldDescription>
                  </Field>
                  <Field className="w-fit">
                    <FieldLabel id="preview-phone-ui-locale-label">
                      手机 UI 语言
                    </FieldLabel>
                    <ToggleGroup
                      aria-labelledby="preview-phone-ui-locale-label"
                      variant="outline"
                      spacing={2}
                      value={phoneUiLocale ? [phoneUiLocale] : []}
                      onValueChange={onPhoneUiLocaleChange}
                    >
                      {phoneUiLocales.map((l) => (
                        <ToggleGroupItem key={l} value={l}>
                          {l}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    <FieldDescription>
                      壳层界面语言（与案例内容无关）。
                    </FieldDescription>
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {active ? (
                    <Badge variant="secondary">
                      运行时 {active.phoneRuntimeVersion}
                    </Badge>
                  ) : null}
                  {active && guidePresent ? (
                    <Badge variant="success">指南就绪</Badge>
                  ) : active ? (
                    <Badge variant="warning">指南待生成</Badge>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!active}
                    onClick={() => setDialogueOpen(true)}
                  >
                    <HugeiconsIcon
                      icon={Message01Icon}
                      data-icon="inline-start"
                    />
                    系统对话
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!active}
                    onClick={() => setReloadKey((k) => k + 1)}
                  >
                    <HugeiconsIcon
                      icon={RefreshIcon}
                      data-icon="inline-start"
                    />
                    重新加载
                  </Button>
                </div>
              </div>

              {active && iframeSrc ? (
                <div className="flex flex-col gap-2">
                  <div
                    className={cn(
                      "w-full",
                      besideEditor
                        ? "max-w-[360px]"
                        : "mx-auto max-w-[420px]",
                    )}
                  >
                    <AspectRatio
                      ratio={9 / 16}
                      className="overflow-hidden rounded-xl border border-border bg-muted/30"
                    >
                      <iframe
                        key={`${active.versionId}-${phoneUiLocale}-${reloadKey}-${reloadNonce}`}
                        title={`手机预览 (json ${active.locale}, ui ${phoneUiLocale})`}
                        src={iframeSrc}
                        className="absolute inset-0 size-full bg-background"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
                        referrerPolicy="no-referrer"
                      />
                    </AspectRatio>
                  </div>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {iframeSrc}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {!isLoading && !isError && locales.length > 0 && !besideEditor ? (
        <Card>
          <CardHeader>
            <CardTitle>线索指南</CardTitle>
            <CardDescription>
              内部运营 QA 用。按结案题列出小手机里的线索位置与说明。手机 JSON
              就绪后生成。
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!active ? null : guidePresent ? (
              <ScrollArea className="h-[min(70vh,720px)] rounded-md border border-border bg-muted/20">
                <div className="p-4">
                  <Markdown markdown={guideMarkdown} />
                </div>
              </ScrollArea>
            ) : (
              <Empty className="border border-border py-8">
                <EmptyHeader>
                  <EmptyTitle>线索指南尚未生成</EmptyTitle>
                  <EmptyDescription>
                    流水线在 refine_phone 成功后会跑线索指南步骤。较旧的运行可继续流水线或重新启动以生成指南。
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </CardContent>
        </Card>
      ) : null}

      <SystemDialogueSheet
        open={dialogueOpen}
        onOpenChange={setDialogueOpen}
        versionId={active?.versionId ?? null}
      />
    </div>
  );
}
